<template>
	<v-container grid-list-md fluid fill-height class="semantic-search-page">
		<v-layout column>
			<v-flex shrink class="semantic-search-header">
				<v-layout row wrap justify-center>
					<v-flex md12 lg8 xl6 class="semantic-search-header__search-bar">
						<v-text-field
							v-model="searchField"
							hide-details
							outline
							clearable
							append-icon="mdi-magnify"
							label="What are you looking for?"
							:disabled="loading"
							@keyup.enter="search"
							@keyup.esc="clear"
							@click:append="search"
						></v-text-field>
					</v-flex>
				</v-layout>
			</v-flex>

			<v-flex shrink>
				<v-layout row wrap justify-center>
					<v-flex md12 lg8 xl6>
						<v-divider class="my-4"></v-divider>
					</v-flex>
				</v-layout>
			</v-flex>

			<v-flex class="semantic-search-results">
				<v-layout row wrap justify-center>
					<template v-if="loading">
						<v-progress-circular indeterminate color="primary"></v-progress-circular>
					</template>

					<p class="semantic-search-placeholder" v-else-if="!revisions.length && !summary">
						Search across Plant's existing content to find what you are looking for...
					</p>

					<v-flex v-else md12 lg8 xl6>
						<div class="semantic-search-overview">
							<img src="/img/bertha/bertha-full-multicolor-LightMode.svg" height="12" />
							<p>Overview</p>
						</div>

						<p class="semantic-search-summary">{{ summary }}</p>

						<p class="mb-2" v-if="revisions.length">Related contents:</p>

						<v-card 
							v-for="(revision, index) in revisions" 
							:key="index"
							class="mb-2"
							style="cursor: pointer"
							title="Open content"
							@click="openContent(revision)"
						>
							<v-card-title class="pb-2">
								<v-layout row align-center class="semantic-search-revision">
									<ContentTypeIcon :type="lowercaseFirstLetter(revision.contentType)" height="40px" />
									<p class="custom-id">{{ revision.contentCustomId }}</p>
									<p class="revision-id">Revision {{ revision.revisionId }}</p>
								</v-layout>
							</v-card-title>

							<v-card-text class="semantic-search-text">
								"<p>{{ revision.text }}</p>"
							</v-card-text>
						</v-card>
					</v-flex>
				</v-layout>
			</v-flex>
			
			<v-flex v-if="revisions.length || summary" shrink>
				<v-layout row wrap justify-center align-center class="semantic-search-disclaimer">
					<img src="/img/bertha/bertha-mini-multicolor-LightMode.svg" height="20" />
					<p>AI-generated content should be independently verified.</p>
				</v-layout>
			</v-flex>
		</v-layout>
	</v-container>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import { STORE_USER_NAME, STATE_TOKEN } from '@/store_constants/user';
import { ACTION_NOTIFICATIONS_ERROR, STORE_NOTIFICATIONS_NAME } from '@/store_constants/notifications';
import AIService from '@/services/AIService';
import { ContentTypeIcon } from "plant-common";

export default {
	name: 'SemanticSearch',
	components: { ContentTypeIcon },
	data() {
		return {
			loading: false,
			aiService: null,
			searchField: '',
			revisions: [],
			summary: ''
		};
	},
	async created() {
		this.aiService = new AIService(this.token);
	},
	provide() {
		return {
			aiService: () => this.aiService
		};
	},
	computed: {
		...mapState(STORE_USER_NAME, {
			token: STATE_TOKEN
		})
	},
	methods: {
		...mapActions(STORE_NOTIFICATIONS_NAME, {
      error: ACTION_NOTIFICATIONS_ERROR
    }),
		async search() {
			if (!this.searchField.trim()) return;

			try {
				this.loading = true;
				const result = await this.aiService.search(this.searchField, 10);
				this.revisions = result.response.revisions;
				this.summary = result.response.summary;
			} catch (error) {
				this.error({ message: 'The search cannot be performed. Please try again.' });
			} finally {
				this.loading = false;
			}
		},
		clear() {
			this.searchField = '';
		},
		lowercaseFirstLetter(str) {
			return str.charAt(0).toLowerCase() + str.slice(1);
		},
		openContent(revision) {
			const { libraryId, contentId, revisionId } = revision;

			const majorRevision = revisionId.split('.')[0];
			const mminorRevision = revisionId.split('.')[1];

			window.open(`/content/${libraryId}/${contentId}/${majorRevision}/${mminorRevision}`);
		}
	}
};
</script>

<style scoped>
.semantic-search-page {
	height: calc(100vh - 66px);
	overflow: hidden;
}

.semantic-search-results {
	overflow-y: auto;
}

.semantic-search-placeholder {
	font-size: larger;
	opacity: 0.72;
}

.semantic-search-overview {
	display: flex;
	column-gap: 0.25rem;
	align-items: baseline;
	margin-bottom: 1rem;
}

.semantic-search-overview p {
	margin-bottom: 0;
	opacity: 0.72;
	font-size: small;
}

.semantic-search-summary {
	font-size: medium;
	margin-bottom: 2rem;
	white-space: pre-line;
}

.semantic-search-revision {
	column-gap: 1rem;
}

.semantic-search-revision .custom-id,
.semantic-search-revision .revision-id {
	margin-bottom: 0;
}

.semantic-search-revision .custom-id {
	font-weight: 500;
	font-size: medium;
}

.semantic-search-text {
	padding-top: 0;
	padding-left: 72px;
	font-style: italic;
	opacity: 0.8;
	display: flex;
}

.semantic-search-text p {
	margin-bottom: 0;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

.semantic-search-disclaimer {
	column-gap: 0.25rem;
	padding-top: 1rem;
}

.semantic-search-disclaimer p {
	margin-bottom: 0;
	opacity: 0.72;
	font-size: small;
}
</style>
